import urlJoin from 'url-join'
import {omit} from '@cullylarson/f'
import {bearer, responseData} from '@@client/lib/request'
import {paramUrl} from '@@client/lib/url'

const haveNonTokenParamErrors = (paramErrors, extraFieldsToOmit = []) => Object.values(omit(['checkoutToken', 'shippingToken', 'salesTaxToken', 'paymentToken', 'storePickupOptionsToken', ...extraFieldsToOmit], paramErrors)).filter(x => x && x.length > 0).length > 0

// adds a 'please see errors below' message if there are paramErrors and no other error messages
const paramErrorsGeneralMessage = (errors, paramErrors, extraFieldsToOmit = []) => {
    // if there already are errors, then don't need to add anything
    if(errors && errors.length) return {errors}

    return haveNonTokenParamErrors(paramErrors, extraFieldsToOmit)
        // if we have param errors that aren't tokens, then we need to display an error about it
        ? {errors: ['There were some problems with the values provided. Please see the error messages below.']}
        // no errors, no param errors
        : {errors: []}
}

export const getStorePickupOptions = (tokenCache, apiUrl, checkoutToken) => {
    const query = {
        checkoutToken,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/store-pickup-options')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                }
            }
        })
}

export const getCanHomeShip = (tokenCache, apiUrl, checkoutToken) => {
    const query = {
        checkoutToken,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/can-home-ship')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                }
            }
        })
}

export const getHomeShippingEstimate = (tokenCache, apiUrl, shippingAddress, checkoutToken) => {
    const query = {
        checkoutToken,
        ...shippingAddress,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/shipping-estimate')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                    ...paramErrorsGeneralMessage(data.errors, data.paramErrors),
                }
            }
        })
}

export const getStorePickup = (tokenCache, apiUrl, checkoutToken, storePickupOptionsToken, pickupStoreId) => {
    const query = {
        checkoutToken,
        storePickupOptionsToken,
        pickupStoreId,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/store-pickup')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                    ...paramErrorsGeneralMessage(data.errors, data.paramErrors),
                }
            }
        })
}

export const getCheckoutToken = (tokenCache, apiUrl, inventoryIds, offerInfos, warrantyInfos) => {
    const query = {
        inventoryIds,
        offers: offerInfos,
        warranties: warrantyInfos,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/initialize')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                }
            }
        })
}

export const getSalesTax = (tokenCache, apiUrl, {
    checkoutToken,
    shippingToken,
}) => {
    const query = {
        checkoutToken,
        shippingToken,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/sales-tax')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                }
            }
        })
}

export const payWithBandrAccount = (tokenCache, apiUrl, checkoutToken, salesTaxToken, {
    giftCardNumber,
    giftCardAmount,
    bandrAccountName,
    bandrAccountNumber,
    bandrAccountPoNumber,
}) => {
    const query = {
        checkoutToken,
        salesTaxToken,
        giftCardNumber,
        giftCardAmount,
        bandrAccountName,
        bandrAccountNumber,
        bandrAccountPoNumber,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/payment-bandr-account')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                    ...paramErrorsGeneralMessage(data.errors, data.paramErrors, ['giftCardAmount', 'giftCardNumber']),
                }
            }
        })
}

export const payWithPaypal = (tokenCache, apiUrl, checkoutToken, salesTaxToken, {
    giftCardNumber,
    giftCardAmount,
    paypalReturnUrl,
    paypalCancelUrl,
}) => {
    const query = {
        checkoutToken,
        salesTaxToken,
        giftCardNumber,
        giftCardAmount,
        paypalReturnUrl,
        paypalCancelUrl,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/payment-paypal')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                }
            }
        })
}

export const payWithAffirm = (tokenCache, apiUrl, checkoutToken, salesTaxToken, {
    giftCardNumber,
    giftCardAmount,
    affirmReturnUrl,
    affirmCancelUrl,
    billingName,
    billingAddress1,
    billingAddress2,
    billingCity,
    billingState,
    billingCountry,
    billingZip,
    billingEmail,
    billingPhone,
    shippingName,
    shippingAddress1,
    shippingAddress2,
    shippingCity,
    shippingState,
    shippingCountry,
    shippingZip,
}) => {
    const query = {
        checkoutToken,
        salesTaxToken,
        giftCardNumber,
        giftCardAmount,
        affirmReturnUrl,
        affirmCancelUrl,
        billingName,
        billingAddress1,
        billingAddress2,
        billingCity,
        billingState,
        billingCountry,
        billingZip,
        billingEmail,
        billingPhone,
        shippingName,
        shippingAddress1,
        shippingAddress2,
        shippingCity,
        shippingState,
        shippingCountry,
        shippingZip,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/payment-affirm')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                }
            }
        })
}

export const getCreditCardKey = (tokenCache, apiUrl, checkoutToken, salesTaxToken) => {
    const query = {
        checkoutToken,
        salesTaxToken,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/credit-card-key')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                }
            }
        })
}

export const payWithCreditCard = async (tokenCache, apiUrl, checkoutToken, salesTaxToken, {
    giftCardNumber,
    giftCardAmount,
    creditCardTransient,
    creditCardName,
    creditCardExpirationMonth,
    creditCardExpirationYear,
    billingName,
    billingAddress1,
    billingAddress2,
    billingCity,
    billingState,
    billingCountry,
    billingZip,
}) => {
    const query = {
        checkoutToken,
        salesTaxToken,
        giftCardNumber,
        giftCardAmount,
        creditCardTransient,
        creditCardName,
        creditCardExpirationMonth,
        creditCardExpirationYear,
        billingName,
        billingAddress1,
        billingAddress2,
        billingCity,
        billingState,
        billingCountry,
        billingZip,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/payment-credit-card')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                    ...paramErrorsGeneralMessage(data.errors, data.paramErrors, ['giftCardAmount', 'giftCardNumber']),
                }
            }
        })
}

export const completeOrder = (tokenCache, apiUrl, checkoutToken, shippingToken, salesTaxToken, paymentToken, {
    contactName,
    contactEmail,
    contactPhone,
    comments,
}) => {
    const query = {
        checkoutToken,
        shippingToken,
        salesTaxToken,
        paymentToken,
        contactName,
        contactEmail,
        contactPhone,
        comments,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/complete')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                }
            }
        })
}

export const fulfillPaypalOrder = (tokenCache, apiUrl, paypalOrderId) => {
    const query = {
        paypalOrderId,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/paypal-complete')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                }
            }
        })
}

export const cancelPaypalOrder = (tokenCache, apiUrl, paypalOrderId) => {
    const query = {
        paypalOrderId,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/paypal-cancel')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                }
            }
        })
}

export const fulfillAffirmOrder = (tokenCache, apiUrl, affirmOrderId) => {
    const query = {
        affirmOrderId,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/affirm-complete')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                }
            }
        })
}

export const cancelAffirmOrder = (tokenCache, apiUrl, affirmOrderId) => {
    const query = {
        affirmOrderId,
    }

    const url = urlJoin(apiUrl, '/api/v1/checkout/affirm-cancel')

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'post',
            body: JSON.stringify(query),
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                }
            }
        })
}

export const checkGuestEmail = (tokenCache, apiUrl, {
    email,
}) => {
    const query = {
        email,
    }

    const url = paramUrl(urlJoin(apiUrl, '/api/v1/checkout/check-guest-email'), query)

    return tokenCache.get()
        .then(token => fetch(url, {
            method: 'get',
            headers: {
                'Authorization': bearer(token),
                'Content-Type': 'application/json',
            },
        }))
        .then(responseData)
        .then(({response, data}) => {
            if(response.ok) {
                return {
                    success: true,
                    ...data,
                }
            }
            else {
                return {
                    success: false,
                    ...data,
                }
            }
        })
}
